import { Badge } from "@/components/ui/badge";
import type { Player } from "@/lib/api/rooms";
import { calcAverage, voteDistribution } from "@/lib/poker";
import { cn } from "@/lib/utils";

export function ResultsPanel({
  players,
  votes,
  showAverage,
}: {
  players: Player[];
  /** Card per player id. */
  votes: Map<string, string>;
  showAverage: boolean;
}) {
  const values = [...votes.values()];
  const average = calcAverage(values);

  return (
    <div
      className="border-t border-border/50 bg-card/80 px-6 py-5 backdrop-blur"
      data-testid="results"
    >
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="flex flex-wrap justify-center gap-3">
            {voteDistribution(values).map(([value, count]) => (
              <div key={value} className="flex flex-col items-center gap-1">
                <div className="flex h-20 w-14 items-center justify-center rounded-lg border-2 border-primary bg-primary text-lg font-bold text-primary-foreground">
                  {value}
                </div>
                <Badge variant="secondary" className="text-xs">
                  {count} vote{count > 1 ? "s" : ""}
                </Badge>
              </div>
            ))}
          </div>

          {showAverage && average !== null && (
            <div className="flex flex-col items-center gap-1 border-l border-border/50 pl-6">
              <span className="text-xs tracking-wider text-muted-foreground uppercase">
                Average
              </span>
              <span className="text-3xl font-bold text-primary" data-testid="average">
                {average}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {players.map((player) => {
            const vote = votes.get(player.id);
            return (
              <div
                key={player.id}
                className="flex items-center gap-1.5 rounded-full bg-secondary/50 px-3 py-1 text-xs"
              >
                <span className="text-muted-foreground">{player.name}:</span>
                <span
                  className={cn("font-semibold", vote ? "text-primary" : "text-muted-foreground")}
                >
                  {vote || "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
